import { t } from 'i18next';
import { useMutation, useQuery } from 'react-query';

import { importService } from 'bom/services';
import {
  IImportNotValidPartsPayload,
  IImportPart,
  IImportPartsPayload,
  IImportPartsStatus,
  IImportPayload,
  IUomListPayload,
  IUomListRow,
  IValidateManufacturers,
  IValidateParts,
  IValidateUoms,
} from 'bom/types';
import { NotificationPosition } from 'core/enums';
import { useHandleErrors, useInfiniteQuery, useQueryChecker } from 'core/hooks';
import { GetManyResponse } from 'core/types';

export const useGetBomImportPreviewStatus = (
  requestPayload: IImportPayload,
  enabled = false,
  onSuccess: (data: IImportPartsStatus) => void,
  onError: () => void
) =>
  useQueryChecker<IImportPartsStatus>({
    fetchData: importService.createImportPartsStatus,
    queryKey: 'bomImportPreview',
    requestPayload,
    enabled,
    isFetchDataFinished: (data: IImportPartsStatus) => data?.finished,
    isFetchDataFailed: (data: IImportPartsStatus) => data?.success === false,
    onSuccess,
    options: {
      loading: { message: t('messageBomImportPreviewInProgress') },
      error: { title: t('errorTitleBomImportPreview') },
      notificationPosition: NotificationPosition.DRAWER,
    },
    onError,
  });

export const useGetBomImportValidateManufacturers = (
  requestPayload: IImportPayload,
  enabled = false,
  onSuccess?: (data?: IValidateManufacturers) => void
) => {
  const { data, isError, isLoading } = useQuery(
    ['bomImportValidateManufacturers', requestPayload],
    ({ signal }) => importService.importValidateManufacturers(requestPayload, signal),
    { enabled, onSuccess }
  );

  return { record: data || null, isError, isLoading };
};

export const useGetBomImportValidateParts = (
  requestPayload: IImportPayload,
  enabled = false,
  onSuccess?: (data?: IValidateParts) => void
) => {
  const { data, isError, isLoading } = useQuery(
    ['bomImportValidateParts', requestPayload],
    ({ signal }) => importService.importValidateParts(requestPayload, signal),
    { enabled, onSuccess }
  );

  return { record: data || null, isError, isLoading };
};

export const useGetBomImportValidateUoms = (
  requestPayload: IImportPayload,
  enabled = false,
  onSuccess?: (data?: IValidateUoms) => void
) => {
  const { data, isError, isLoading } = useQuery(
    ['bomImportValidateUoms', requestPayload],
    ({ signal }) => importService.importValidateUoms(requestPayload, signal),
    { enabled, onSuccess }
  );

  return { record: data || null, isError, isLoading };
};

export const useGetUomList = (
  requestPayload: IUomListPayload,
  enabled = false,
  onSuccess?: (data: GetManyResponse<IUomListRow>) => void
) =>
  useInfiniteQuery<IUomListRow>(importService.getUomList, 'bomImportUomList', requestPayload, {
    enabled,
    onSuccess,
    fetchAllPages: true,
  });

export const useGetBomImportNotValidParts = (
  requestPayload: IImportNotValidPartsPayload,
  enabled = false,
  onSuccess?: (data: GetManyResponse<IImportPart>) => void
) =>
  useInfiniteQuery<IImportPart>(
    importService.importNotValidParts,
    'bomImportNotValidParts',
    requestPayload,
    {
      enabled,
      onSuccess,
      fetchAllPages: true,
    }
  );

export const useGetBomImportPartColumns = (requestPayload: IImportPayload, enabled = false) => {
  const { data, isError, isLoading } = useQuery(
    ['bomImportPartColumns', requestPayload],
    ({ signal }) => importService.getImportPartColumns(requestPayload, signal),
    { enabled }
  );

  return { columns: data || [], isError, isLoading };
};

export const useGetBomImportParts = (requestPayload: IImportPartsPayload, enabled = false) =>
  useInfiniteQuery<IImportPart>(importService.getImportParts, 'bomImportParts', requestPayload, {
    enabled,
    fetchAllPages: true,
  });

export const useGetBomImportStatus = (
  requestPayload: IImportPayload,
  enabled = false,
  onSuccess: (data: IImportPartsStatus) => void,
  onSettled?: (data: IImportPartsStatus) => void,
  onError?: (data: IImportPartsStatus) => void
) =>
  useQueryChecker<IImportPartsStatus>({
    fetchData: importService.importPartsStatus,
    queryKey: 'bomImport',
    requestPayload,
    enabled,
    isFetchDataFinished: (data: IImportPartsStatus) => data?.finished,
    isFetchDataFailed: (data: IImportPartsStatus) => data?.success === false,
    onSuccess,
    options: {
      error: { title: t('errorTitleImportParts'), disableAutoClose: true },
      notificationPosition: NotificationPosition.DRAWER,
    },
    onSettled,
    onError,
  });

export const useBomImportActions = () => ({
  ...useCreateColumnAliases(),
  ...useCreateImportParts(),
  ...useCreateImportPartManufacturers(),
  ...useUpsertImportParts(),
  ...useImportParts(),
  ...useSaveUoms(),
});

const useCreateColumnAliases = () => {
  const { errors } = useHandleErrors({
    loading: { message: t('messageBomImportPreviewInProgress') },
    error: { title: t('errorTitleBomImportPreview') },
    position: NotificationPosition.DRAWER,
  });

  const { mutateAsync } = useMutation(importService.createColumnAliases, {
    ...errors,
  });

  return { createColumnAliases: mutateAsync };
};

const useCreateImportParts = () => {
  const { errors } = useHandleErrors({
    loading: { message: t('messageBomImportPreviewInProgress') },
    error: { title: t('errorTitleBomImportPreview') },
    position: NotificationPosition.DRAWER,
  });

  const { mutateAsync } = useMutation(importService.createImportParts, {
    ...errors,
  });

  return { createImportParts: mutateAsync };
};

const useCreateImportPartManufacturers = () => {
  const { errors } = useHandleErrors({
    loading: { message: t('messageCreateImportPartManufacturersInProgress') },
    error: { title: t('errorTitleCreateImportPartManufacturers') },
    position: NotificationPosition.DRAWER,
  });

  const { mutateAsync } = useMutation(importService.createImportPartManufacturers, {
    ...errors,
  });

  return { createImportPartManufacturers: mutateAsync };
};

const useUpsertImportParts = () => {
  const { errors } = useHandleErrors({
    error: { title: t('errorTitleBomImportUpdateParts') },
    position: NotificationPosition.DRAWER,
  });

  const { mutateAsync } = useMutation(importService.upsertImportParts, {
    ...errors,
  });

  return { upsertImportParts: mutateAsync };
};

const useImportParts = () => {
  const { errors } = useHandleErrors({
    loading: { message: t('messageImportPartsInProgress') },
    error: { title: t('errorTitleImportParts') },
    position: NotificationPosition.DRAWER,
    doNotClearNotification: true,
  });

  const { mutateAsync } = useMutation(importService.importParts, {
    ...errors,
  });

  return { importParts: mutateAsync };
};

const useSaveUoms = () => {
  const { errors } = useHandleErrors({
    error: { title: t('errorTitleSaveUoms') },
    position: NotificationPosition.DRAWER,
  });

  const { mutateAsync } = useMutation(importService.saveUoms, {
    ...errors,
  });

  return { saveUoms: mutateAsync };
};
